import { Flag, Language, LanguageSwitcher as LanguageSwitcherComponent } from '@jablotron/lib.spa-components';
import { testId } from '@jablotron/lib.spa-core/testing';
import { useLanguage, useTranslate } from '@jablotron/lib.spa-lang';
import { useMatch, useRouter } from '@tanstack/react-router';
import { useMemo } from 'react';
// Not ~/entities/blog/api: the header is in the main chunk, and api.ts would bring every article's meta with it.
import { getPostLanguages } from '~/entities/blog/languages';
import { toLocalisedPath } from '~/entities/urls/localised-path';
import { type LanguageCode, languageCodes, languageNames } from '~/texts/languages';
import { Languages, Texts } from '~/texts/runtime';
import { useHydrated } from '~/utils/useHydrated';

/**
 * `pathname` without a language prefix: the router keeps a prefixed address that is no page (`/cs/categories/x`,
 * shown as not-found) as it is, and switching it must not stack a second prefix in front.
 */
function withoutLanguagePrefix(pathname: string): string {
  const [, first, ...rest] = pathname.split('/');

  return (languageCodes as readonly string[]).includes(first) ? `/${rest.join('/')}` : pathname;
}

/**
 * Switches the language by going to the same page's address in the chosen language (PDR-0019): the UI
 * follows the address (LanguageProvider). On a blog article it offers only the languages the article
 * exists in, everywhere else all of them.
 *
 * On the server and in the render that hydrates server HTML it shows only the current language's flag (the
 * library's closed trigger): the library picks popover or drawer with @uidotdev/usehooks' useMediaQuery, which
 * throws there ("useMediaQuery is a client-only hook"). The library's switcher takes over right after hydration.
 */
export const LanguageSwitcher: React.FC = () => {
  const translate = useTranslate();
  const [currentLanguage] = useLanguage();
  const router = useRouter();
  const hydrated = useHydrated();
  const articleSlug = useMatch({ from: '/blog_/$slug', shouldThrow: false, select: (match) => match.params.slug });
  const languages = useMemo(() => {
    const articleLanguages = articleSlug ? getPostLanguages(articleSlug) : [];

    return articleLanguages.length > 0 ? articleLanguages : [...Languages];
  }, [articleSlug]);

  return (
    <div className="flex h-10 items-center" {...testId(`current-language-${currentLanguage}`)}>
      {hydrated ? (
        <LanguageSwitcherComponent
          {...testId('language-switcher')}
          name="language"
          languages={languages as Language[]}
          value={currentLanguage as Language}
          title={translate(Texts.SwitchLanguage)}
          selectLabel={translate(Texts.ChangeLanguage)}
          confirmButtonText={translate(Texts.Confirm)}
          dismissButtonText={translate(Texts.Cancel)}
          onConfirmed={(language) => {
            // router.navigate to the same internal path would not change the address: the internal path is the
            // same in every language, only the rewrite's output differs. Going to the public address does,
            // also from a prefix-less one (dev server), which moves it to the prefixed form.
            const { pathname, searchStr, hash } = router.state.location;
            const internalPath = `${withoutLanguagePrefix(pathname)}${searchStr}${hash ? `#${hash}` : ''}`;
            router.history.push(toLocalisedPath(language as LanguageCode, internalPath));
          }}
        />
      ) : (
        <button
          type="button"
          className="inline-flex cursor-pointer rounded-sm"
          aria-label={languageNames[currentLanguage as LanguageCode] ?? currentLanguage}
          {...testId('language-switcher-placeholder')}
        >
          <Flag className="h-4 w-[21px]" language={currentLanguage as Language} />
        </button>
      )}
    </div>
  );
};
